import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PackageCheck, AlertTriangle } from 'lucide-react';
import { useEmpSaldoMedicao, SaldoMedicaoItem } from '@/hooks/useEmpSaldoMedicao';
import { useEmpPlanilhaVersoes } from '@/hooks/useEmpPlanilha';

import { fmtCurrency as fmtCur } from '@/lib/formatters';
const fmtQty = (v: number, u: string) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ${u}`;

interface SaldosContratoPanelProps {
  contratoId: string;
}

export const SaldosContratoPanel: React.FC<SaldosContratoPanelProps> = ({ contratoId }) => {
  const { data: versoes = [] } = useEmpPlanilhaVersoes(contratoId);
  const versaoVigente = versoes.find(v => v.status === 'vigente');
  const { data: saldos = [], isLoading } = useEmpSaldoMedicao(versaoVigente?.id ?? null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
        Carregando...
      </div>
    );
  }

  if (!versaoVigente) {
    return (
      <div className="flex flex-col items-center justify-center h-40 text-muted-foreground text-sm gap-2">
        <AlertTriangle className="h-8 w-8 opacity-40" />
        <p>Nenhuma planilha vigente neste contrato.</p>
      </div>
    );
  }

  if (saldos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 text-muted-foreground text-sm gap-2">
        <PackageCheck className="h-8 w-8 opacity-40" />
        <p className="text-center">Nenhum item disponível neste contrato.<br />Importe a planilha contratual para visualizar saldos.</p>
      </div>
    );
  }

  const itensProximoLimite = saldos.filter(s => {
    const pct = s.quantidade_contratada > 0 ? (s.qtd_medida_acumulada / s.quantidade_contratada) : 0;
    return pct >= 0.9;
  });
  const itensEsgotados = saldos.filter(s => s.saldo_contratual <= 0);
  const saldoTotalRemanescente = saldos.reduce((sum, s) => sum + (s.saldo_contratual * s.preco_unitario), 0);

  return (
    <div className="space-y-3">
      {/* Items list */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {saldos.map((item) => (
          <SaldoItemCard key={item.planilha_item_id} item={item} />
        ))}
      </div>

      {/* Summary footer */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Saldo total remanescente</p>
              <p className="text-xl font-bold text-foreground">{fmtCur(saldoTotalRemanescente)}</p>
            </div>
            <div className="text-right space-y-0.5">
              <p className="text-xs text-muted-foreground">{saldos.length} itens</p>
              {itensProximoLimite.length > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {itensProximoLimite.length} acima de 90%
                </p>
              )}
              {itensEsgotados.length > 0 && (
                <p className="text-xs text-destructive">
                  {itensEsgotados.length} esgotado{itensEsgotados.length > 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const SaldoItemCard: React.FC<{ item: SaldoMedicaoItem }> = ({ item }) => {
  const pctMedido = item.quantidade_contratada > 0
    ? (item.qtd_medida_acumulada / item.quantidade_contratada) * 100
    : 0;
  const isWarning = pctMedido >= 90 && pctMedido < 100;
  const isExhausted = item.saldo_contratual <= 0;
  const valorRemanescente = item.saldo_contratual * item.preco_unitario;

  return (
    <div className="p-3 rounded-md border bg-card hover:bg-muted/30 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-medium truncate flex-1">
          {item.codigo_item} — {item.descricao}
        </p>
        {isExhausted && (
          <Badge variant="destructive" className="shrink-0 text-xs">Esgotado</Badge>
        )}
        {isWarning && !isExhausted && (
          <Badge variant="warning" className="shrink-0 text-xs">&lt;10%</Badge>
        )}
      </div>

      {/* Semantic progress bar: ≥70% green, 40-69% blue, <40% amber */}
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary mb-2">
        <div
          className={`h-full rounded-full transition-all ${
            pctMedido >= 70 ? 'bg-success' : pctMedido >= 40 ? 'bg-progress' : 'bg-warning'
          }`}
          style={{ width: `${Math.min(pctMedido, 100)}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Contratado: {fmtQty(item.quantidade_contratada, item.unidade)}
        </span>
        <span className={`font-semibold ${
          pctMedido >= 70 ? 'text-success' : pctMedido >= 40 ? 'text-progress' : 'text-warning'
        }`}>
          {pctMedido.toFixed(0)}%
        </span>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground mt-0.5">
        <span>Medido: {fmtQty(item.qtd_medida_acumulada, item.unidade)} · Saldo: {fmtQty(item.saldo_contratual, item.unidade)}</span>
      </div>
      <p className="text-xs font-semibold text-foreground mt-1">
        {fmtCur(valorRemanescente)} remanescente
      </p>
    </div>
  );
};
